/* Footer (umbrella ZHELTA homepage) — clean, responsive, dark-gradient
 * footer mirroring the Academy and Systems pattern. Brand block spans 2
 * cols; Divisions, Company, and Help fill the rest. Compliance strip at
 * the bottom carries the year, LinkedIn, Terms/Privacy and the
 * conditional admin login (only shown to already-signed-in admins). */
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin, Facebook, Instagram, Youtube, Phone, Globe } from 'lucide-react';
import ZheltaLogo from './ZheltaLogo';
import { useI18n } from '../context/I18nContext';
import { useCurrency, appendCurrency } from '../context/CurrencyContext';
import { ZHELTA_SHOP_URL } from '../mock';

export default function Footer() {
  const { t } = useI18n();
  const { currency } = useCurrency();

  // The admin dashboard is intentionally NOT linked from the public homepage
  // footer — staff reach it via the private internal URL only.
  const shopUrl = appendCurrency(ZHELTA_SHOP_URL, currency);
  const f = t?.home?.cards?.footer || {};

  return (
    <footer
      className="text-zinc-800 bg-[#F5F5F7]"
      data-testid="home-footer"
    >
      <section className="max-w-[1200px] mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Brand block — spans 2 cols on md+ */}
        <div className="md:col-span-2">
          <Link to="/" className="inline-flex items-center gap-3 mb-4" data-testid="home-footer-brand">
            <ZheltaLogo size={36} />
            <div className="leading-none">
              <div className="text-[18px] font-extrabold tracking-[0.2em]">ZHELTA</div>
              <div className="text-[10px] font-semibold tracking-[0.3em] text-[#f5a623]">.COM</div>
            </div>
          </Link>
          <p className="text-[14px] text-zinc-600 leading-relaxed max-w-[480px]">
            Three integrated divisions — <strong className="text-zinc-900">Systems</strong>,{' '}
            <strong className="text-zinc-900">Services</strong>, and{' '}
            <strong className="text-zinc-900">Academy</strong> — bringing enterprise IT,
            verified hardware, and practical education together for what's next.
          </p>

          <ul className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2 text-zinc-500 text-[13px]">
            <li>
              <a
                href="tel:+19147681947"
                data-testid="home-footer-phone"
                className="inline-flex items-center gap-1.5 hover:text-[#c9a227]"
              >
                <Phone className="w-3.5 h-3.5 text-[#c9a227]" /> 914-768-1947
              </a>
            </li>
            <li>
              <a
                href="mailto:sales@zhelta.com"
                data-testid="home-footer-email"
                className="inline-flex items-center gap-1.5 hover:text-[#c9a227]"
              >
                <Mail className="w-3.5 h-3.5 text-[#c9a227]" /> sales@zhelta.com
              </a>
            </li>
            <li className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#c9a227]" /> New York, NY · USA
            </li>
          </ul>
        </div>

        {/* Divisions */}
        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Divisions</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><Link to="/systems"   className="hover:text-[#c9a227]">ZHELTA Systems</Link></li>
            <li>
              <Link to="/services" className="hover:text-[#c9a227]">
                ZHELTA Services (Shop)
              </Link>
            </li>
            <li><Link to="/academy"           className="hover:text-[#c9a227]">ZHELTA Academy</Link></li>
            <li><Link to="/verify"            className="hover:text-[#c9a227]">Verify a certificate</Link></li>
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Company</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><Link to="/about"     className="hover:text-[#c9a227]">About us</Link></li>
            <li><Link to="/support"   className="hover:text-[#c9a227]">Support</Link></li>
            <li><Link to="/systems/contact" className="hover:text-[#c9a227]" data-testid="home-footer-contact">Contact</Link></li>
            <li><Link to="/careers"   className="hover:text-[#c9a227]">Careers</Link></li>
          </ul>
        </div>
      </section>

      {/* Compliance strip */}
      <section className="border-t border-zinc-200">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[12px] text-zinc-500">
            <Link
              to="/copyright"
              data-testid="home-footer-copyright"
              className="hover:text-[#c9a227] transition"
              title="Copyright & Terms of Use"
            >
              © {new Date().getFullYear()} ZHELTA LLC. {f.rights || 'All rights reserved.'}
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-zinc-500 text-[11.5px]">
            <Link to="/terms"   className="hover:text-[#c9a227]">{f.terms   || 'Terms of use'}</Link>
            <span className="text-zinc-300">·</span>
            <Link to="/privacy" className="hover:text-[#c9a227]">{f.privacy || 'Privacy policy'}</Link>
            <span className="text-zinc-300">·</span>
            <Link to="/cookies" data-testid="home-footer-cookies" className="hover:text-[#c9a227]">Cookies</Link>

            <span className="text-zinc-300 hidden sm:inline">·</span>
            <a
              href="https://www.linkedin.com/company/zhelta-llc/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="home-footer-linkedin"
              className="inline-flex items-center hover:text-[#c9a227]"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61562259287313"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="home-footer-facebook"
              className="inline-flex items-center hover:text-[#c9a227]"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/zhelta_support?igsh=dTdqbGo4MWZqcW1z&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="home-footer-instagram"
              className="inline-flex items-center hover:text-[#c9a227]"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://youtube.com/@zheltallc?si=x-hOD-nWZpWempMK"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              data-testid="home-footer-youtube"
              className="inline-flex items-center hover:text-[#c9a227]"
            >
              <Youtube className="w-4 h-4" />
            </a>
            <a
              href="https://www.zhelta.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-[#c9a227]"
            >
              <Globe className="w-3 h-3" /> zhelta.com
            </a>
          </div>
        </div>
      </section>
    </footer>
  );
}
